/**
 * ShareContactWithFamilyAction — button + modal on the personal contact detail
 * page that shares a contact into a family (nav-decisions §1: contacts are
 * personal-first and shared into a family exactly like logins, via the
 * `share-item-v1` ECIES wrap).
 *
 * Difference from the login share (`ShareWithFamilyAction`): the picker is
 * **categories**, not circles. A contact is filed under a family category
 * (whose circle sets its visibility) or left in the **Family contacts**
 * catch-all (no category). We denormalize the chosen category's `circleId`
 * onto the shared item's envelope so the server's circle read-filter applies,
 * and put the `categoryId` inside the (encrypted) item so the family Binder
 * groups it correctly.
 *
 * Semantics: the share is a fresh-UUID copy under `ORG#<orgId>` — the sharer's
 * personal contact in My Contacts is untouched (they see it in both places).
 */

import { useEffect, useState } from 'react';
import type { VaultItem } from '@heybesafe/shared';
import { useSafe } from '../context/SafeContext';
import { useFamilyKeys } from '../context/FamilyKeysContext';
import { shareItemWithFamily, listCategories } from '../api/client';
import { base64UrlToBytes } from '../crypto/base64url';
import { decryptCategoryName } from '../crypto/categoryName';
import { prepareShareForFamily } from './prepareShareForFamily';
import { familyKeyAuthentic } from '../pkastore/familyKeyAuth';

interface Cat {
  categoryId: string;
  name: string;
  circleId?: string;
}

export function ShareContactWithFamilyAction({ item }: { item: VaultItem }): JSX.Element | null {
  const { orgs, sync } = useSafe();
  const { getKey } = useFamilyKeys();

  const [isOpen, setIsOpen] = useState(false);
  const [selectedOrgId, setSelectedOrgId] = useState<string>(orgs[0]?.id ?? '');
  const [categories, setCategories] = useState<Cat[]>([]);
  // '' = the Family contacts catch-all (no category).
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastSharedFamilyName, setLastSharedFamilyName] = useState<string | null>(null);

  // Fetch + decrypt the selected family's categories whenever the modal opens
  // or the family changes. A family with no key hydrated / a failed fetch just
  // shows the Family contacts option.
  useEffect(() => {
    if (!isOpen) return;
    const orgId = selectedOrgId || orgs[0]?.id;
    const org = orgs.find((o) => o.id === orgId);
    if (!orgId || !org?.publicKey) {
      setCategories([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      const res = await listCategories(orgId);
      if (cancelled || !res.ok) return;
      const privKey = getKey(orgId);
      if (!privKey) {
        setCategories([]);
        return;
      }
      const pubBytes = base64UrlToBytes(org.publicKey);
      const decrypted: Cat[] = [];
      for (const s of res.data.categories) {
        try {
          const { name } = await decryptCategoryName(s, privKey, pubBytes);
          decrypted.push({ categoryId: s.categoryId, name, circleId: s.circleId });
        } catch {
          /* skip undecryptable */
        }
      }
      if (!cancelled) setCategories(decrypted.sort((a, b) => a.name.localeCompare(b.name)));
    })();
    return () => { cancelled = true; };
  }, [isOpen, selectedOrgId, orgs, getKey]);

  if (orgs.length === 0) return null;
  if (item.orgId) return null; // already a family item

  async function handleShare(): Promise<void> {
    const orgId = selectedOrgId || orgs[0]?.id;
    const org = orgs.find((o) => o.id === orgId);
    if (!org) return;
    if (!org.publicKey) {
      setError('Family is missing its public key. Try refreshing.');
      return;
    }
    // PKA (A2): authenticate the family public key before wrapping to it.
    if (!familyKeyAuthentic(org)) {
      setError("Could not verify this family's security key — do not share. Ask a family admin to sign in to the latest version.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const chosen = selectedCategoryId
        ? categories.find((c) => c.categoryId === selectedCategoryId)
        : undefined;
      // categoryId rides inside the encrypted item; circleId (the category's
      // scope) rides the plaintext envelope for the server's read-filter.
      const plainForShare: VaultItem = { ...item, categoryId: chosen?.categoryId };
      const encrypted = await prepareShareForFamily(plainForShare, base64UrlToBytes(org.publicKey), null, orgId);
      const itemToShare = chosen?.circleId ? { ...encrypted, circleId: chosen.circleId } : encrypted;
      const result = await shareItemWithFamily(itemToShare);
      if (!result.ok) {
        setError('Share failed. Please try again.');
        return;
      }
      setLastSharedFamilyName(org.name);
      setIsOpen(false);
      await sync();
    } catch (e) {
      console.error('ShareContactWithFamilyAction: share failed', e);
      setError('Share failed. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  function handleOpen(): void {
    setError(null);
    setLastSharedFamilyName(null);
    setSelectedOrgId(orgs[0]?.id ?? '');
    setSelectedCategoryId('');
    setCategories([]);
    setIsOpen(true);
  }

  const isSingleFamily = orgs.length === 1;
  const selectedOrg = orgs.find((o) => o.id === (selectedOrgId || orgs[0]?.id));

  return (
    <>
      <button
        type="button"
        id="share-contact-open-button"
        onClick={handleOpen}
        className="px-3 py-1.5 text-xs text-ink-secondary hover:text-ink hover:bg-surface-raised rounded-lg transition-colors"
      >
        Share with family
      </button>

      {lastSharedFamilyName && !isOpen && (
        <span role="status" className="text-xs text-green-600 ml-2">
          Shared with {lastSharedFamilyName}. Your personal copy stays in My Contacts.
        </span>
      )}

      {isOpen && (
        <div role="dialog" aria-modal="true" className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-surface border border-divider rounded-xl p-6 max-w-md w-full">
            <h2 className="text-base font-medium text-ink mb-4">
              {isSingleFamily ? `Share ${item.name} with ${orgs[0].name}?` : 'Pick a family to share with'}
            </h2>

            {!isSingleFamily && (
              <select
                aria-label="Family"
                value={selectedOrgId}
                onChange={(e) => { setSelectedOrgId(e.target.value); setSelectedCategoryId(''); setCategories([]); }}
                disabled={busy}
                className="w-full bg-surface-raised border border-divider rounded-lg px-3 py-2 text-sm mb-4"
              >
                {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            )}

            {!isSingleFamily && selectedOrg && (
              <p className="text-sm text-ink mb-4">
                Share <span className="font-medium">{item.name}</span> with{' '}
                <span className="font-medium">{selectedOrg.name}</span>?
              </p>
            )}

            <div className="mb-4">
              <label htmlFor="category-picker" className="block text-xs font-medium text-ink-secondary mb-1">
                File under
              </label>
              <select
                id="category-picker"
                aria-label="File under"
                value={selectedCategoryId}
                onChange={(e) => setSelectedCategoryId(e.target.value)}
                disabled={busy}
                className="w-full bg-surface-raised border border-divider rounded-lg px-3 py-2 text-sm"
              >
                <option value="">Family contacts (no binder)</option>
                {categories.map((c) => (
                  <option key={c.categoryId} value={c.categoryId}>{c.name}</option>
                ))}
              </select>
            </div>

            <p className="text-xs text-ink-secondary mb-4">
              The whole family (or the category’s circle) will see this contact. Your personal copy stays in My Contacts.
            </p>

            {error && <p role="alert" className="text-xs text-red-500 mb-3">{error}</p>}

            <div className="flex gap-2 justify-end">
              <button type="button" id="share-contact-cancel-button"
                onClick={() => !busy && setIsOpen(false)} disabled={busy} className="px-3 py-1.5 text-xs text-ink-secondary">
                Cancel
              </button>
              <button type="button" id="share-contact-confirm-button"
                onClick={handleShare} disabled={busy} className="px-3 py-1.5 text-xs bg-hbs-primary hover:bg-hbs-primary-hover text-hbs-on-primary rounded-lg font-medium">
                {busy ? 'Sharing…' : 'Share'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
